import React, { useState, useEffect } from 'react';
import { SimpleEditorWrapper } from '@/components/notes/SimpleEditorWrapper';
import { cn } from '@/lib/utils';
import { useNotesManager } from '@/contexts/NotesManager';
import { Note } from '@/contexts/NotesManager';

interface NoteContentPanelProps {
  note: Note;
  onEditingChange?: (isEditing: boolean) => void; // kept for compatibility
  className?: string;
}

export function NoteContentPanel({ note, onEditingChange, className }: NoteContentPanelProps) {
  const { updateNote, deleteNote } = useNotesManager();

  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content);
  const [subtitle, setSubtitle] = useState(note.subtitle || '');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [titleWasManuallySet, setTitleWasManuallySet] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize component state when note changes
  useEffect(() => {
    setTitle(note.title);
    setContent(note.content);
    setSubtitle(note.subtitle || '');
    setHasUnsavedChanges(false);
    setTitleWasManuallySet(note.title !== 'Untitled Note' && note.title.trim() !== '');
    setIsInitialized(true);
  }, [note.id]);

  // Track unsaved changes but only after initialization
  useEffect(() => {
    if (!isInitialized) return;
    const hasChanges = title !== note.title || content !== note.content || subtitle !== (note.subtitle || '');
    setHasUnsavedChanges(hasChanges);
  }, [title, content, subtitle, note.title, note.content, note.subtitle, isInitialized]);

  // Auto-save with improved debouncing
  useEffect(() => {
    if (!hasUnsavedChanges || !isInitialized) return;
    
    const saveTimeout = setTimeout(() => {
      try {
        updateNote(note.id, { title, content, subtitle });
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to save note:', error);
      }
    }, 800); // Reduced delay for better UX

    return () => clearTimeout(saveTimeout);
  }, [hasUnsavedChanges, title, content, subtitle, note.id, updateNote, isInitialized]);

  const stripHtml = (html: string) => html.replace(/<[^>]+>/g, '');

  const handleContentChange = (newContent: string) => {
    if (!isInitialized) return; // Prevent changes during initialization
    
    setContent(newContent);

    // Auto-generate title only if user hasn't manually set one and content is substantial
    if (!titleWasManuallySet && (!title || title === 'Untitled Note')) {
      const plainText = stripHtml(newContent).replace(/\s+/g, ' ').trim();
      if (plainText && plainText.length >= 10) { // Require more content before auto-titling
        const words = plainText.split(' ').filter(word => word.length > 0);
        if (words.length >= 2) {
          const autoTitle = words.slice(0, 3).join(' '); // Use first 3 words
          if (autoTitle.length <= 50) { // Keep titles reasonable
            setTitle(autoTitle);
            setTitleWasManuallySet(true);
          }
        }
      }
    }
  };

  const handleTitleChange = (newTitle: string) => {
    setTitleWasManuallySet(true); // Mark as manually set
    setTitle(newTitle);
  };

  return (
    <div className={cn('h-full flex flex-col', className)}>
      <SimpleEditorWrapper
        title={title}
        onTitleChange={handleTitleChange}
        subtitle={subtitle}
        onSubtitleChange={setSubtitle}
        value={content}
        onChange={handleContentChange}
        placeholder="Start writing your note..."
        className="h-full"
      />
    </div>
  );
}
